import type {ExperimentAssignmentEnvelopeV1, ExperimentScope} from './store-types'

const MAX_EXPERIMENT_PATH_LENGTH = 512
const MAX_STRING_VALUE_LENGTH = 4096
const MAX_FEATURE_VARIABLES = 100
const MAX_ASSIGNMENT_CONTEXT_LENGTH = 16_384
const MAX_SERIALIZED_ENVELOPE_LENGTH = 256 * 1024

const unavailableScope: ExperimentScope = Object.freeze({status: 'unavailable'})

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function utf8Length(value: string): number {
  return new TextEncoder().encode(value).byteLength
}

export function isWithinAssignmentContextLimit(value: string): boolean {
  return utf8Length(value) <= MAX_ASSIGNMENT_CONTEXT_LENGTH
}

export function isWithinSerializedEnvelopeLimit(value: unknown): boolean {
  const serialized = JSON.stringify(value)
  return serialized !== undefined && utf8Length(serialized) <= MAX_SERIALIZED_ENVELOPE_LENGTH
}

function isFeatureVariable(value: unknown): value is string {
  return typeof value === 'string' && utf8Length(value) <= MAX_STRING_VALUE_LENGTH
}

/**
 * Merges opaque context tokens while keeping the first value seen for each variant.
 *
 * For example, `A:one;B:two` merged with `A:three;C:four` keeps `A:one`.
 */
export function mergeAssignmentContext(current: string, incoming: string): string {
  const entries = new Map<string, string>()
  for (const context of [current, incoming]) {
    for (const entry of context.split(';')) {
      if (!entry) continue
      const separator = entry.indexOf(':')
      const variant = separator === -1 ? entry : entry.slice(0, separator)
      if (!entries.has(variant)) entries.set(variant, entry)
    }
  }
  return [...entries.values()].join(';')
}

/**
 * Replaces an available scope's context with the store's accumulated assignment context.
 *
 * The store uses this after installing or committing a scope so later requests include context
 * collected from earlier assignments. Unavailable scopes are returned unchanged.
 */
export function withAssignmentContext(scope: ExperimentScope, assignmentContext: string): ExperimentScope {
  if (scope.status === 'unavailable') return scope
  return Object.freeze({
    status: 'available',
    envelope: Object.freeze({
      ...scope.envelope,
      assignment: Object.freeze({...scope.envelope.assignment, assignment_context: assignmentContext}),
    }),
  })
}

/**
 * Checks whether independently initialized app roots received the same complete assignment.
 *
 * This comparison includes assignment context, so scopes that differ only by context are not
 * equivalent and cannot safely share the document store.
 */
export function equivalentScopes(left: ExperimentScope, right: ExperimentScope): boolean {
  if (left.status !== right.status) return false
  if (left.status === 'unavailable' || right.status === 'unavailable') return true
  const leftAssignment = left.envelope.assignment
  const rightAssignment = right.envelope.assignment
  if (
    leftAssignment.assignment_context !== rightAssignment.assignment_context ||
    leftAssignment.data_version !== rightAssignment.data_version ||
    leftAssignment.failed !== rightAssignment.failed
  ) {
    return false
  }

  const leftVariables = Object.entries(leftAssignment.feature_variables)
  const rightVariables = Object.entries(rightAssignment.feature_variables)
  return (
    leftVariables.length === rightVariables.length &&
    leftVariables.every(([path, value]) => rightAssignment.feature_variables[path] === value)
  )
}

/** Returns an unavailable scope when any part of the untrusted assignment envelope is invalid or oversized. */
export function decodeExperimentAssignment(value: unknown): ExperimentScope {
  if (!isRecord(value) || value.schema_version !== 1 || !isRecord(value.assignment)) {
    return unavailableScope
  }

  const {assignment} = value
  if (
    !isRecord(assignment.feature_variables) ||
    typeof assignment.assignment_context !== 'string' ||
    !isWithinAssignmentContextLimit(assignment.assignment_context) ||
    typeof assignment.data_version !== 'number' ||
    !Number.isSafeInteger(assignment.data_version) ||
    assignment.data_version < 0 ||
    typeof assignment.failed !== 'boolean'
  ) {
    return unavailableScope
  }

  // Paths come from the wire, so a null prototype keeps names such as "__proto__" as ordinary data.
  const featureVariables = Object.create(null) as Record<string, string>
  const entries = Object.entries(assignment.feature_variables)
  if (entries.length > MAX_FEATURE_VARIABLES) return unavailableScope
  for (const [path, featureValue] of entries) {
    if (!path || utf8Length(path) > MAX_EXPERIMENT_PATH_LENGTH || !isFeatureVariable(featureValue)) {
      return unavailableScope
    }
    featureVariables[path] = featureValue
  }
  const envelope: ExperimentAssignmentEnvelopeV1 = Object.freeze({
    schema_version: 1,
    assignment: Object.freeze({
      feature_variables: Object.freeze(featureVariables),
      assignment_context: assignment.assignment_context,
      data_version: assignment.data_version,
      failed: assignment.failed,
    }),
  })
  if (!isWithinSerializedEnvelopeLimit(envelope)) return unavailableScope
  return Object.freeze({status: 'available', envelope})
}

/** Creates the fallback scope used while an experiment assignment is absent or still tentative. */
export function unavailableExperimentScope(): ExperimentScope {
  return unavailableScope
}
